import React, { useEffect } from 'react'
import { useDispatch } from 'react-redux'
import {
  Box,
  Button,
  Card,
  CardContent,
  Container,
  Grid,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Typography,
} from '@mui/material'
import CheckIcon from '@mui/icons-material/Check'
import { fetchTrackEvent, ProductAnalyticsEventTypes } from 'src/redux/productAnalytics'

const PRICING_TIERS = [
  {
    name: 'Free',
    subtitle: 'Match Counts Only',
    price: '$0/mo',
    features: [
      'See how many candidates match your jobs',
      'Post unlimited jobs',
      'Basic employer profile',
    ],
    actionLabel: null,
  },
  {
    name: 'Professional',
    subtitle: 'Full Access',
    price: 'Contact Sales',
    features: [
      'Everything in Free',
      'View candidate profiles',
      'Direct messaging',
      'Priority support',
    ],
    actionLabel: 'Contact Sales',
  },
  {
    name: 'Enterprise',
    subtitle: 'Custom',
    price: 'Contact Sales',
    features: [
      'Everything in Professional',
      'API access',
      'Dedicated account manager',
      'Custom integrations',
    ],
    actionLabel: 'Contact Sales',
  },
]

const PricingTierCard = ({ tier, onPlanClick }) => (
  <Card variant='outlined' sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
    <CardContent sx={{ flexGrow: 1 }}>
      <Typography variant='h5' sx={{ fontWeight: 700, mb: 0.5 }}>
        {tier.name}
      </Typography>
      <Typography variant='subtitle2' color='text.secondary' sx={{ mb: 2 }}>
        {tier.subtitle}
      </Typography>
      <Typography variant='h4' sx={{ fontWeight: 700, mb: 2 }}>
        {tier.price}
      </Typography>
      <List dense disablePadding>
        {tier.features.map(feature => (
          <ListItem key={feature} disableGutters>
            <ListItemIcon sx={{ minWidth: 32 }}>
              <CheckIcon color='success' fontSize='small' />
            </ListItemIcon>
            <ListItemText primary={feature} />
          </ListItem>
        ))}
      </List>
    </CardContent>
    <Box sx={{ p: 2, pt: 0 }}>
      {tier.actionLabel ? (
        <Button variant='contained' fullWidth onClick={() => onPlanClick(tier.name)}>
          {tier.actionLabel}
        </Button>
      ) : (
        <Button variant='outlined' fullWidth disabled>
          Current Plan
        </Button>
      )}
    </Box>
  </Card>
)

const PricingPage = () => {
  const dispatch = useDispatch()

  useEffect(() => {
    dispatch(
      fetchTrackEvent({
        eventName: 'pricing_page_viewed',
        eventType: ProductAnalyticsEventTypes.ACTION,
      }),
    )
  }, [dispatch])

  const handlePlanClick = (tierName) => {
    dispatch(
      fetchTrackEvent({
        eventName: 'pricing_page_plan_clicked',
        eventType: ProductAnalyticsEventTypes.BUTTON_CLICK,
        eventData: { planName: tierName },
      }),
    )
  }

  return (
  <Container maxWidth='lg' sx={{ py: 6 }}>
    <Typography variant='h4' align='center' sx={{ fontWeight: 700, mb: 1 }}>
      Choose the right plan for your organization
    </Typography>
    <Typography variant='body1' align='center' color='text.secondary' sx={{ mb: 5 }}>
      Start for free and upgrade when you're ready to connect with candidates.
    </Typography>
    <Grid container spacing={3} justifyContent='center'>
      {PRICING_TIERS.map(tier => (
        <Grid item xs={12} sm={6} md={4} key={tier.name}>
          <PricingTierCard tier={tier} onPlanClick={handlePlanClick} />
        </Grid>
      ))}
    </Grid>
  </Container>
  )
}

export default PricingPage
